import type { SmRole } from "@/domain/sm-models";
import {
  clearAuthToken,
  clientRequestHeaders,
  purgeLegacyAuthStorage,
  setAuthToken,
} from "@/lib/sm-api";
import { shouldSmPersistBearerInClient } from "@/lib/sm-session-bridge";
import { notifyAppRefresh } from "@/lib/sm-app-events";
import { fail, ok, type ServiceResult } from "./service-result";

export interface AuthSessionUser {
  id: string;
  email: string;
  username?: string | null;
  name: string;
  role: SmRole;
  tenantId: string | null;
  staffPermissions?: Record<string, boolean> | null;
  operationalRoleId?: string | null;
  locationIds?: string[];
  locationRoles?: Record<string, string>;
  avatarUrl?: string | null;
  themeMode?: "dark" | "light" | "system" | null;
}

export type AuthRegisterInput = {
  email: string;
  password: string;
  name: string;
  /** SCRUM-183: Cloudflare Turnstile token (frontend challenge) */
  cfTurnstileToken?: string;
};

async function parseJson<T>(r: Response): Promise<T & { error?: string }> {
  return (await r.json().catch(() => ({}))) as T & { error?: string };
}

function isLocalE2EBrowser(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
}

export const authService = {
  async getCurrentUser(): Promise<AuthSessionUser | null> {
    if (typeof window === "undefined") return null;
    try {
      // During local visual/E2E runs the freshly generated Bearer token is
      // deliberately bootstrapped into localStorage. Do not rely only on the
      // session transport resolver here: this call is the app's first session
      // gate, so it must send the exact tenant context used by the E2E setup.
      const headers = new Headers(clientRequestHeaders());
      const token = localStorage.getItem("sm.authToken");
      const tenantId = localStorage.getItem("sm.tenantId");
      const localE2E = isLocalE2EBrowser();
      if (token && localE2E) headers.set("Authorization", `Bearer ${token}`);
      if (tenantId) headers.set("X-Tenant-Id", tenantId);

      const r = await fetch("/api/auth/session", {
        // In dual transport the server resolves the cookie before Bearer.
        // A stale cookie can therefore mask the verified E2E Bearer token.
        // Local visual/E2E session bootstrap intentionally uses Bearer only.
        credentials: localE2E ? "omit" : "include",
        headers,
      });
      if (!r.ok) {
        // Keep the E2E bootstrap token available for diagnostics/retries when
        // the local app is still initializing. Production keeps the existing
        // 401 -> clear behavior.
        if (r.status === 401 && !localE2E) clearAuthToken();
        return null;
      }
      const data = await parseJson<{ user: AuthSessionUser }>(r);
      return data.user ?? null;
    } catch {
      return null;
    }
  },

  async register(input: AuthRegisterInput): Promise<ServiceResult<AuthSessionUser>> {
    const email = input.email.trim().toLowerCase();
    if (!email || !input.password || !input.name)
      return fail("Completá todos los campos", "AUTH_INPUT_INVALID");
    if (input.password.length < 6)
      return fail("La contraseña debe tener al menos 6 caracteres", "AUTH_WEAK_PASSWORD");

    const r = await fetch("/api/auth/register", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password: input.password, name: input.name.trim(), cfTurnstileToken: input.cfTurnstileToken }),
    });
    const data = await parseJson<{ token?: string; user?: AuthSessionUser; error?: string }>(r);
    if (!r.ok || !data.user) {
      return fail(data.error ?? "No se pudo registrar", "AUTH_REGISTER_FAILED");
    }
    if (data.token && shouldSmPersistBearerInClient()) setAuthToken(data.token);
    else purgeLegacyAuthStorage();
    notifyAppRefresh();
    return ok(data.user);
  },

  async login(
    login: string,
    password: string,
    opts?: { tenantId?: string; tenantSlug?: string; cfTurnstileToken?: string },
  ): Promise<
    ServiceResult<
      | { user: AuthSessionUser; token: string }
      | {
          needsTenantPick: true;
          tenants: Array<{ id: string; name: string; slug: string }>;
        }
    >
  > {
    const id = login.trim();
    if (!id || !password) return fail("Usuario y contraseña son obligatorios", "AUTH_INPUT_INVALID");

    const body: Record<string, string> = { login: id, password };
    if (opts?.tenantId?.trim()) body.tenantId = opts.tenantId.trim();
    if (opts?.tenantSlug?.trim()) body.tenantSlug = opts.tenantSlug.trim();
    if (opts?.cfTurnstileToken) body.cfTurnstileToken = opts.cfTurnstileToken;

    const r = await fetch("/api/auth/login", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await parseJson<{
      token?: string;
      user?: AuthSessionUser;
      error?: string;
      code?: string;
      tenants?: Array<{ id: string; name: string; slug: string }>;
    }>(r);

    if (r.status === 409 && data.code === "AMBIGUOUS_TENANT") {
      return ok({
        needsTenantPick: true,
        tenants: Array.isArray(data.tenants) ? data.tenants : [],
      });
    }

    if (!r.ok || !data.user) {
      return fail(data.error ?? "Credenciales inválidas", "AUTH_INVALID");
    }
    if (data.token && shouldSmPersistBearerInClient()) setAuthToken(data.token);
    else purgeLegacyAuthStorage();
    notifyAppRefresh();
    return ok({ token: data.token ?? "", user: data.user });
  },

  async logout(): Promise<void> {
    clearAuthToken();
    purgeLegacyAuthStorage();
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
        headers: clientRequestHeaders(),
      });
    } catch {
      // Error de red: las cookies se limpiarán igualmente en el próximo login;
      // no bloqueamos el cierre de sesión del cliente por esto.
    }
  },
};
